/**
 * Stale-chunk recovery. The app lazy-loads route chunks with content-hashed
 * filenames; a deploy replaces those hashes, so a tab left open across a deploy
 * 404s on its next dynamic import and crashes into the ErrorBoundary ("Oops").
 *
 * Recovery is a single, loop-guarded auto-reload: refresh once to pick up the
 * freshly-deployed build. The guard (a sessionStorage timestamp) stops a
 * genuinely-broken build from thrashing the tab in an endless reload loop — if
 * we reloaded moments ago, callers fall through to the normal error UI instead.
 *
 * Used from two places: the `vite:preloadError` listener in the entry (Vite
 * fires it on a failed dynamic-import preload) and the ErrorBoundary (for the
 * import rejection that reaches React once the chunk fails to evaluate).
 */

const RELOAD_TS_KEY = 'wh:stale-chunk-reload-at'
const RELOAD_WINDOW_MS = 10_000

// Vite/browser phrasings for "a dynamically imported module failed to load".
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/i

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const err = error as { name?: unknown; message?: unknown }
  const name = typeof err.name === 'string' ? err.name : ''
  const message = typeof err.message === 'string' ? err.message : String(error)
  return name === 'ChunkLoadError' || CHUNK_ERROR_RE.test(message)
}

function recentlyReloaded(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY)) || 0
    return Date.now() - last < RELOAD_WINDOW_MS
  } catch {
    // sessionStorage blocked (private mode / strict cookie policy): treat as
    // "not recently reloaded" so a first recovery attempt can still happen.
    return false
  }
}

/**
 * Whether it is currently safe to auto-reload. A fallback renderer consults
 * this to choose "silently refresh" over "show Oops": false means we just
 * reloaded, so the new build is likely also broken — show the error instead.
 */
export function canReloadForStaleChunk(): boolean {
  return !recentlyReloaded()
}

/**
 * Loop-guarded reload. Refreshes to the new build at most once per
 * RELOAD_WINDOW_MS; a no-op if we reloaded within that window.
 */
export function reloadForStaleChunk(): void {
  if (recentlyReloaded()) return
  try {
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()))
  } catch {
    // sessionStorage blocked: reload anyway (best-effort, unguarded once).
  }
  window.location.reload()
}
